import { useState, FC } from 'react'
import { Box, Stack, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import moment from 'moment'
import clsx from 'clsx'
import useStyles from '../_styles/PatientFormModificationsHistoryStyles'
import { HistoryRowDetail, PatientFormModificationsHistoryRowProps } from './_props'
import { actionTranslation, getDetailType, getReadableDetail, isDetailExpendable } from './_utils'
import PatientIdentityStatus from '../PatientIdentity/PatientIdentityStatus'
import { capitalizeFirstLetter } from '../../../../utils/stringUtils'
import sxs from './sxs'
import { defaultPalette } from '../../../../theme/idsTheme'
import PatientFormModificationsHistoryRowDialog from './PatientFormModificationsHistoryRowDialog'
import { generateRandomGuiID } from '../../../../utils/commonUtils'

const getValue = (value: string | undefined) => {
    if (!value) return '-'
    const max = 40

    return `${capitalizeFirstLetter(value).substring(0, max)} ${value.length >= max ? '...' : ''}`
}

const PatientFormModificationsHistoryRow: FC<PatientFormModificationsHistoryRowProps> = ({
    row: { id, label, date, actionType, insIndentityStatus, actionDetails, insiDetail },
    handleExpandPanel,
    expandedPanel,
    mostRecent = false,
}) => {
    const classes = useStyles()
    const [previewDetailItem, setPreviewDetailItem] = useState<HistoryRowDetail | undefined>(undefined)

    const action = actionType ? actionTranslation[actionType] : undefined
    const dateText = date ? `le ${moment(date).format('DD/MM/YYYY à HH:mm')}` : 'date inconnue'

    const computedDetail = getReadableDetail(insiDetail)

    const shouldDisplayDetails = actionType === 'UPDATE' && actionDetails && actionDetails.length > 0

    const displayDetail = (detailItem: HistoryRowDetail) => {
        if (isDetailExpendable(detailItem)) {
            setPreviewDetailItem(detailItem)
        }
    }

    /**
     * translate and compute here
     * needs to be done by type
     */
    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box className={classes.PatientFormModificationsHistoryRow}>
                    <Accordion
                        className={clsx(
                            classes.PatientFormModificationsHistoryRowAccordion,
                            mostRecent && classes.PatientFormModificationsHistoryMostRecentRowAccordion,
                        )}
                        sx={{
                            '& .MuiAccordionSummary-content': {
                                cursor: shouldDisplayDetails ? 'cursor' : 'default',
                            },
                        }}
                        expanded={expandedPanel === id || expandedPanel === true}
                        onChange={actionType === 'UPDATE' ? handleExpandPanel(id) : undefined}
                    >
                        <AccordionSummary
                            expandIcon={
                                shouldDisplayDetails && (
                                    <ArrowDropDownIcon sx={{ color: '#313336', fontSize: '20px', top: '-40px' }} />
                                )
                            }
                            className={classes.PatientFormModificationsHistoryRowAccordionSummary}
                        >
                            <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                                <Stack direction="row" gap=".5rem">
                                    <Box sx={sxs.PatientFormModificationsHistoryRowTag}>
                                        {action && (
                                            <Typography
                                                className={classes.PatientFormModificationsHistoryItemAction}
                                                sx={{
                                                    background: action.colors.background,
                                                    color: action.colors.color,
                                                }}
                                            >
                                                {action.action}
                                            </Typography>
                                        )}
                                        {insIndentityStatus && <PatientIdentityStatus status={insIndentityStatus} />}
                                    </Box>
                                    <Box sx={sxs.PatientFormModificationsHistoryRowContent}>
                                        <Box sx={sxs.PatientFormModificationsHistoryRowContentMain}>
                                            <Typography className={classes.PatientFormModificationsHistoryItem}>
                                                <Stack direction="row" gap=".5rem">
                                                    <strong>{capitalizeFirstLetter(dateText)}</strong>
                                                    <div>par</div>
                                                    <strong>{label ?? 'utilisateur inconnu'}</strong>
                                                </Stack>
                                            </Typography>
                                        </Box>
                                        {computedDetail && computedDetail.length > 0 && (
                                            <Box sx={sxs.PatientFormModificationsHistoryRowContentDetail}>
                                                {computedDetail.map((item) => (
                                                    <Box
                                                        sx={[
                                                            sxs.PatientFormModificationsHistoryRowContentDetailItem,
                                                            {
                                                                cursor: isDetailExpendable(item)
                                                                    ? 'pointer'
                                                                    : 'default',
                                                            },
                                                        ]}
                                                        key={item.label}
                                                        onClick={() => {
                                                            displayDetail(item)
                                                        }}
                                                    >
                                                        <Typography
                                                            fontSize=".6rem"
                                                            color={defaultPalette.typography.mediumDark}
                                                        >
                                                            {capitalizeFirstLetter(item.label)}
                                                        </Typography>
                                                        <Typography
                                                            fontSize=".7rem"
                                                            color={defaultPalette.typography.mediumDark}
                                                        >
                                                            {getDetailType(item.label) === 'history' ? (
                                                                <Typography
                                                                    component="span"
                                                                    fontSize=".7rem"
                                                                    sx={{
                                                                        textDecoration: 'underline',
                                                                    }}
                                                                >
                                                                    Voir l'historique INS
                                                                </Typography>
                                                            ) : (
                                                                getValue(item?.value)
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Stack>
                                {shouldDisplayDetails && <Typography fontSize={13}>Voir le détail</Typography>}
                            </Stack>
                        </AccordionSummary>
                        {shouldDisplayDetails && (
                            <AccordionDetails className={classes.PatientFormModificationsHistoryRowAccordionDetails}>
                                <div className={classes.PatientFormModificationsHistoryAccordionDetailsContent}>
                                    {actionDetails?.map((fieldName) => (
                                        <span key={generateRandomGuiID()}>{fieldName}</span>
                                    ))}
                                </div>
                            </AccordionDetails>
                        )}
                    </Accordion>
                </Box>
            </Stack>
            <PatientFormModificationsHistoryRowDialog
                open={previewDetailItem !== undefined}
                onClose={() => setPreviewDetailItem(undefined)}
                item={previewDetailItem}
            />
        </>
    )
}

export default PatientFormModificationsHistoryRow
